import { useRef } from "react";
import TemplateCard from "../Cards/TemplateCard";
import videoSrc from "../../assets/cta.mp4";
import { useScrollAnimation } from "../../hooks/useScrollAnimation";

// Import video sources
import Video_0_mp4 from "../../assets/Video_0.mp4";
import Video_1_mp4 from "../../assets/Video_1.mp4";
import Video_2_webm from "../../assets/Video_2.webm";
import Video_3_mp4 from "../../assets/Video_3.mp4";
import Video_4_mp4 from "../../assets/Video_4.mp4";
import Video_5_webm from "../../assets/Video_5.webm";
import Video_6_webm from "../../assets/Video_6.webm";

const CTA = ({ templateImages = [] }) => {
  const defaultVideos = [
    { webm: null, mp4: Video_0_mp4 }, // 0
    { webm: null, mp4: Video_1_mp4 }, // 1
    { webm: Video_2_webm, mp4: null }, // 2
    { webm: null, mp4: Video_3_mp4 }, // 3
    { webm: null, mp4: Video_4_mp4 }, // 4
    { webm: Video_5_webm, mp4: null }, // 5
    { webm: Video_6_webm, mp4: null }, // 6
  ];

  const videosToUse =
    templateImages.length === 7 ? templateImages : defaultVideos;

  const { ref: containerRef, isVisible } = useScrollAnimation({ threshold: 0.1 });

  return (
    <div ref={containerRef} className="w-full relative py-1 md:py-2">
      <div className="w-full z-[-1] absolute inset-0">
        <video
          className="absolute top-0 left-0 w-full h-full object-cover"
          autoPlay
          muted
          loop
          playsInline
        >
          <source src={videoSrc} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      </div>

      {/* Centered Foreground layout with staggered animations */}
      <div className="relative z-10 flex items-center justify-center">
        <div className="w-[96%] sm:w-[94%] md:w-[92%] lg:w-[90%] xl:w-[88%] 2xl:w-[85%] h-fit flex flex-col md:flex-row p-1 md:p-2 gap-1 sm:gap-1.5 md:gap-2 lg:gap-3">
          {/* LEFT COLUMN - 1 big video + 2 small videos */}
          <div className="flex flex-col w-full md:w-1/2 gap-1 sm:gap-1.5 md:gap-2 lg:gap-3">
            {/* Top: Big video with 3:2 ratio */}
            <div
              className={`w-full transition-all duration-700 ease-out hover-lift ${
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              }`}
              style={{ aspectRatio: '3/2', transitionDelay: '0ms' }}
            >
              <TemplateCard
                isVideo={true}
                srcWebm={videosToUse[0].webm}
                srcMp4={videosToUse[0].mp4}
                poster={videosToUse[0].poster}
                className="w-full h-full hover:scale-[1.02] transition-transform duration-300"
              />
            </div>

            {/* Bottom: 2 small videos side by side with 4:3 ratio */}
            <div className="flex gap-1 sm:gap-1.5 md:gap-2 lg:gap-3">
              <div
                className={`flex-1 transition-all duration-700 ease-out hover-lift ${
                  isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
                }`}
                style={{ aspectRatio: '4/3', transitionDelay: '150ms' }}
              >
                <TemplateCard
                  isVideo={true}
                  srcWebm={videosToUse[1].webm}
                  srcMp4={videosToUse[1].mp4}
                  poster={videosToUse[1].poster}
                  className="w-full h-full hover:scale-[1.02] transition-transform duration-300"
                />
              </div>
              <div
                className={`flex-1 transition-all duration-700 ease-out hover-lift ${
                  isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
                }`}
                style={{ aspectRatio: '4/3', transitionDelay: '300ms' }}
              >
                <TemplateCard
                  isVideo={true}
                  srcWebm={videosToUse[4].webm}
                  srcMp4={videosToUse[4].mp4}
                  poster={videosToUse[4].poster}
                  className="w-full h-full hover:scale-[1.02] transition-transform duration-300"
                />
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN - 1 big video with 4:3 ratio */}
          <div className="flex flex-col w-full md:w-1/2">
            <div
              className={`w-full h-full transition-all duration-700 ease-out hover-lift ${
                isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"
              }`}
              style={{ aspectRatio: '4/3', transitionDelay: '200ms' }}
            >
              <TemplateCard
                isVideo={true}
                srcWebm={videosToUse[2].webm}
                srcMp4={videosToUse[2].mp4}
                poster={videosToUse[2].poster}
                className="w-full h-full hover:scale-[1.02] transition-transform duration-300"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CTA;
